import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Request } from "../../models/request/request.model";
import { Status } from "../../models/response/status/status.model";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  AppConfState,
  setAppConf,
} from "../../redux/states/appConf/appConfSlice";
import { setContent } from "../../redux/states/content/contentSlice";
import { setPega } from "../../redux/states/pega/pegaSlice";
import { setStatus } from "../../redux/states/status/statusSlice";
import { ERRORS, GENERIC_ERROR_PAGE } from "../../utils/constants";
import { useAdobe } from "../useAdobe/useAdobe";
import { useApi } from "../useApi/useApi";
import { useCustomNavigation } from "../useCustomNavigation/useCustomNavigation";
import { useGrecaptcha } from "../useGrecaptcha/useGrecaptcha";
import { useLogger } from "../useLogger/useLogger";
import { useOneTrust } from "../useOneTrust/useOneTrust";
import { toast } from "react-toastify";
import { store } from "../../redux/store";

export const useFlow = () => {
  const { customNavigation } = useCustomNavigation();
  const navigate = useNavigate();
  const { configuration, startup, stepLoader, citiesByZip } = useApi();
  const reduxDispatch = useAppDispatch();
  const { log, logError } = useLogger();
  const { getGrecaptchaToken, loadGrecaptchaScript } = useGrecaptcha();
  const appConf = useAppSelector((state) => state.appConf);
  const status = useAppSelector((state) => state.status);
  const { i18n, t } = useTranslation();
  const { loadAdobeScript, fireInitialization, fireFieldError } = useAdobe();
  const { loadOTScript } = useOneTrust();

  const setCaptchaToken = useCallback(async (): Promise<string | null> => {
    if (
      appConf.captchaEnabled &&
      status.step &&
      appConf.captchaProtectedSteps.includes(status.step)
    ) {
      log(
        "log",
        "useFlow.tsx",
        "callSteploader - setCaptchaToken => This step is protected by Invisible Captcha"
      );
      return getGrecaptchaToken();
    }
    log(
      "log",
      "useFlow.tsx",
      "callSteploader - setCaptchaToken => Captcha disabled or step not protected  "
    );
    return null;
  }, [appConf, status.step, log, getGrecaptchaToken]);

  const handleError = useCallback(
    (errStatus: Status, animation: boolean = true) => {
      const errors = ERRORS.filter((err) =>
        errStatus.errorCodes?.includes(err.code)
      );
      const errorFrom = errStatus.errorType > 0 ? "PEGA ERROR" : "BE ERROR";
      if (errors.length > 0) {
        errors.forEach((err) => {
          log(
            "error",
            "useFlow.tsx",
            `handleError =>`,
            `${errorFrom} - ${err.msg} : ${err.code}`
          );
        });
        const navigationErrors = errors.filter((err) => err.navigate != null);
        if (navigationErrors.length > 0 && navigationErrors[0].navigate) {
          customNavigation(navigationErrors[0].navigate, animation);
        }
      } else {
        log(
          "error",
          "useFlow.tsx",
          `handleError =>`,
          `${errorFrom} - Unhandled error codes : ${errStatus.errorCodes}`
        );
        customNavigation(GENERIC_ERROR_PAGE, animation);
      }
    },
    [log, customNavigation]
  );

  const loadConfiguration = useCallback(
    async (flow: string | null, acquisitionpage: string | null) => {
      log("log", "useFlow.tsx", "loadConfiguration - configuration call");
      let resp = await configuration();
      if (resp) {
        let conf = resp.data.status;
        log(
          conf.success ? "log" : "error",
          "useFlow.tsx",
          "loadConfiguration - configuration resp =>",
          resp
        );
        if (conf.success) {
          let data: AppConfState = resp.data.data;
          //Add acquisitionpage variable in page to let adobe choose the proper report suite once loaded
          fireInitialization();
          //Load Adobe connection script
          log("log", "useFlow.tsx", "Loading Adobe Analytics script...");
          loadAdobeScript(data.environment);
          //Load OneTrust connection script (if not in iCornerApp)
          if (acquisitionpage !== "iCornerApp") {
            log("log", "useFlow.tsx", "Loading OT script...");
            loadOTScript(data.environment);
          }
          //Load grecaptcha v3 if captcha are enabled
          if (data.captchaEnabled) {
            log(
              "log",
              "useFlow.tsx",
              "Google captcha enabled. Loading script..."
            );
            loadGrecaptchaScript(data.captchaSiteKey);
          } else {
            log("log", "useFlow.tsx", "Google captcha disabled.");
          }
          //If not in production, enable or disable flow based on flow query param
          data.workflowEnabled =
            flow === "false" && data.environment !== "prod" ? false : true;
          //Update AppConfState
          reduxDispatch(setAppConf(data));
        } else {
          handleError(conf);
        }
      } else {
        logError(
          "useFlow.tsx",
          "loadConfiguration - configuration resp =>",
          resp
        );
      }
    },
    [
      log,
      configuration,
      fireInitialization,
      loadAdobeScript,
      reduxDispatch,
      loadOTScript,
      loadGrecaptchaScript,
      handleError,
      logError,
    ]
  );

  const appStartup = useCallback(
    async (
      resumeToken: string | null,
      crossSellingParam: string | null,
      channelParam: string
    ) => {
      let body: Request = {
        Data: {
          CurrentStep: "",
          CampaignChannel: channelParam,
        },
        Extra: {
          Locale: i18n.resolvedLanguage,
          resumeToken: resumeToken ? resumeToken : undefined,
          csp: crossSellingParam ? crossSellingParam : undefined,
        },
      };
      log("log", "useFlow.tsx", "appStartup - startup req =>", body);
      let resp = await startup(body);
      if (resp) {
        let respStatus = resp.data.status;
        let respPega = resp.data.pega;
        let respContent = resp.data.content;
        log(
          respStatus.success ? "log" : "error",
          "useFlow.tsx",
          "appStartup - startup resp =>",
          resp
        );

        if (respStatus.success) {
          //Update state
          reduxDispatch(setStatus(respStatus));
          reduxDispatch(setPega(respPega));
          reduxDispatch(setContent(respContent));
          //Navigate
          if (respStatus.step) navigate(respStatus.step, { replace: true });
        } else {
          handleError(respStatus, false);
        }
      } else {
        logError("useFlow.tsx", "appStartup - startup resp =>", resp);
      }
    },
    [
      startup,
      i18n.resolvedLanguage,
      reduxDispatch,
      log,
      logError,
      handleError,
      navigate,
    ]
  );

  const callSteploader = useCallback(
    async (body: Request, shouldNavigate = true, step?: string) => {
      const channelStore = store.getState().queryParams.channelParam;
      // Add Channel in Extra
      body.Extra.Channel =
        channelStore === "App" ||
        channelStore === "QR" ||
        channelStore === "Qr" ||
        channelStore === "Mobile"
          ? "Mobile"
          : "Web";
      //If captcha are enabled for the current step, set captchaToken in extra
      let token = await setCaptchaToken();
      if (token) body.Extra.captchaToken = token;
      //Send devCaseId for localhost testing pourpose
      if (
        process.env.REACT_APP_ENV === "dev" &&
        sessionStorage.getItem("devCaseID")
      ) {
        body.Extra.devCaseID = sessionStorage.getItem("devCaseID");
      }
      //Add Locale (currently used) in Extra
      body.Extra.Locale = i18n.resolvedLanguage;
      //Add CurrentStep in Data
      body.Data.CurrentStep = step ? step : status.step;

      log("log", "useFlow.tsx", "callSteploader - stepLoader req =>", body);
      let resp = await stepLoader(body);
      if (resp) {
        let respStatus = resp.data.status;
        let respPega = resp.data.pega;
        let respContent = resp.data.content;

        log(
          respStatus.success ? "log" : "error",
          "useFlow.tsx",
          "callSteploader - stepLoader resp =>",
          resp.data
        );
        if (respStatus.success) {
          //Set devCaseId for localhost testing pourpose
          if (
            process.env.REACT_APP_ENV === "dev" &&
            resp.data.content?.devCaseID
          ) {
            sessionStorage.setItem("devCaseID", resp.data.content.devCaseID);
          }
          //Update state
          reduxDispatch(setStatus(respStatus));
          reduxDispatch(setPega(respPega));
          reduxDispatch(setContent(respContent));

          //Navigate
          if (shouldNavigate && respStatus.step)
            customNavigation(respStatus.step);
        } else {
          handleError(respStatus);
          if (respStatus.errorCodes && respStatus.errorCodes.includes(-2)) {
            let toastMsg =
              t`captchaTooLow.title` + " - " + t`captchaTooLow.description`;
            toast.error(toastMsg, {
              position: "top-right",
              autoClose: 6000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
              type: "error",
            });
            fireFieldError();
          }
        }
      } else {
        logError("useFlow.tsx", "callSteploader - stepLoader resp =>", resp);
      }
      return resp;
    },
    [
      i18n.resolvedLanguage,
      log,
      logError,
      customNavigation,
      reduxDispatch,
      setCaptchaToken,
      status.step,
      stepLoader,
      handleError,
      fireFieldError,
      t,
    ]
  );

  const getCitiesByZip = useCallback(
    async (zip: string) => {
      log("log", "useFlow.tsx", "getCitiesByZip - citiesByZip call");
      let resp = await citiesByZip(zip);
      if (resp) {
        log("log", "useFlow.tsx", "getCitiesByZip - citiesByZip resp =>", resp);
      } else {
        logError("useFlow.tsx", "getCitiesByZip - citiesByZip resp =>", resp);
      }
      return resp;
    },
    [citiesByZip, log, logError]
  );

  return { loadConfiguration, appStartup, callSteploader, getCitiesByZip };
};
